import { Check, CircleAlert, Loader2, MinusCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fmtNumber } from '@/shared/lib/format'
import type { Run, RunStep } from '../api'
import { STAGE_BLURB, STAGE_KIND_LABEL, STEP_STATUS_LABEL, usd } from '../vocab'

/**
 * What the council is doing, right now.
 *
 * ## This is the progress bar, and it is better than one
 *
 * The backend reaches every model through the platform's single rate-limited,
 * circuit-broken network door, whose response is a byte array — so there is no
 * token stream to render. What there is instead is a row per model call, filling
 * in as each one answers.
 *
 * That turns out to be the more useful picture. A cursor says "something is
 * happening". This says *Claude Opus 5 is thinking, GPT-5.1 has answered in 14
 * seconds for a third of a cent, and the red team is Grok because it had to be
 * somebody other than Anthropic.* It also survives a page reload, which a stream
 * does not.
 *
 * ## A skipped red team is shown, not hidden
 *
 * `SKIPPED_NO_DIVERSE_VENDOR` means this deployment has one vendor configured, so
 * nobody could argue with the analyst. The row stays, greyed, with the reason —
 * and the report it belongs to is capped at MEDIUM confidence and says so.
 * Hiding the row would leave a report that looks verified and was not.
 */

const STATUS_ICON: Record<string, typeof Check> = {
  PENDING: MinusCircle,
  RUNNING: Loader2,
  OK: Check,
  FAILED: X,
  SKIPPED_NO_MODEL: CircleAlert,
  SKIPPED_NO_DIVERSE_VENDOR: CircleAlert,
  SKIPPED_BUDGET: CircleAlert,
}

const STATUS_CLASS: Record<string, string> = {
  PENDING: 'text-muted-foreground/50',
  RUNNING: 'text-chocolate animate-spin',
  OK: 'text-emerald-600',
  FAILED: 'text-red-600',
  SKIPPED_NO_MODEL: 'text-amber-600',
  SKIPPED_NO_DIVERSE_VENDOR: 'text-amber-600',
  SKIPPED_BUDGET: 'text-amber-600',
}

export function CouncilBoard({ run, className }: { run: Run; className?: string }) {
  const steps = [...run.steps].sort((a, b) => a.ordinal - b.ordinal)

  if (steps.length === 0) {
    return (
      <div className={cn('rounded-md border border-border px-3 py-4', className)}>
        <div className="flex items-center gap-2 text-erp-sm">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-chocolate" />
          {run.status === 'BUILDING_BRIEF'
            ? 'Assembling the evidence brief. No model has been called yet.'
            : 'Queued. The runner has not picked this up yet.'}
        </div>
        <p className="mt-1 text-erp-xs text-muted-foreground">
          The brief is built from this platform&rsquo;s own data, permission-filtered and
          redacted, before anything is sent anywhere.
        </p>
      </div>
    )
  }

  const vendors = new Set(steps.map((s) => s.vendor).filter(Boolean))

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-erp-xs text-muted-foreground">
        <span>
          {steps.length} step{steps.length === 1 ? '' : 's'} ·{' '}
          {vendors.size} vendor{vendors.size === 1 ? '' : 's'}
        </span>
        <span className="tabular-nums">
          {fmtNumber(run.inputTokens)} in / {fmtNumber(run.outputTokens)} out
        </span>
        <span className="tabular-nums">{usd(run.costUsd)}</span>
      </div>

      <ol className="space-y-1">
        {steps.map((step) => (
          <StepRow key={step.id} step={step} />
        ))}
      </ol>
    </div>
  )
}

function StepRow({ step }: { step: RunStep }) {
  const Icon = STATUS_ICON[step.status] ?? MinusCircle
  const skipped = step.status.startsWith('SKIPPED')
  return (
    <li className={cn(
      'rounded-md border px-2.5 py-2',
      step.status === 'FAILED'
        ? 'border-red-300 bg-red-50/40 dark:bg-red-500/5'
        : skipped
          ? 'border-dashed border-border'
          : 'border-border',
    )}>
      <div className="flex items-start gap-2">
        <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', STATUS_CLASS[step.status])} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-erp-sm font-medium">
              {STAGE_KIND_LABEL[step.stageKind] ?? step.stageKind}
            </span>
            <span className="text-erp-xs text-muted-foreground truncate">{step.label}</span>
            {step.vendor && (
              <span className="text-erp-xs rounded border border-border px-1 py-px text-muted-foreground">
                {step.vendor} · {step.modelKey}
              </span>
            )}
          </div>

          <div className="mt-0.5 text-erp-xs text-muted-foreground">
            {STAGE_BLURB[step.stageKind]}
          </div>

          {(step.status === 'OK') && (
            <div className="mt-0.5 flex flex-wrap gap-x-3 text-erp-xs text-muted-foreground/80 tabular-nums">
              <span>{fmtNumber(step.inputTokens)} in / {fmtNumber(step.outputTokens)} out</span>
              {step.latencyMs != null && <span>{(step.latencyMs / 1000).toFixed(1)}s</span>}
              <span>{usd(step.costUsd)}</span>
            </div>
          )}

          {step.errorMessage && (
            <div className={cn('mt-1 text-erp-xs',
              skipped ? 'text-muted-foreground' : 'text-red-700 dark:text-red-400')}>
              <span className="font-medium">{STEP_STATUS_LABEL[step.status] ?? step.status}</span>
              {' — '}{step.errorMessage}
            </div>
          )}
        </div>
      </div>
    </li>
  )
}
